import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSubmitChapterActivity } from "@/features/myChapterActivity/hooks/useSubmitChapterActivity";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const SubmitChapterActivityForm: React.FC = () => {
  const { form, submitLoading, submitError, submitForm, handleCancel } =
    useSubmitChapterActivity();

  const { handleSubmit, control } = form;

  // Activity type options with labels and values
  const activityTypes = [
    {
      label: "New Member Recruitment",
      value: "New Member Recruitment Activity",
    },
    { label: "Community Service", value: "Community Service Activity" },
    {
      label: "Professional Development",
      value: "Professional Development Activity",
    },
    {
      label: "PACE (Planning to Achieve Collegiate Excellence)",
      value: "PACE (Planning to Achieve Collegiate Excellence)",
    },
    { label: "Member Social Gathering", value: "Social Activity" },
    { label: "Other", value: "Other" },
  ];

  return (
    <MainPageWrapper title="Submit Chapter Activity">
      <div className="mb-6">
        <p className="text-gray-600">
          Submit a new activity for your chapter. All fields marked with * are
          required.
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <Form {...form}>
          <form className="space-y-6" onSubmit={handleSubmit(submitForm)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Activity Name */}
              <FormField
                control={control}
                name="chapterActivityName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter activity name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Activity Date */}
              <FormField
                control={control}
                name="chapterActivityDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Activity Type */}
              <FormField
                control={control}
                name="activityType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Type *</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select activity type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {activityTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* NSCS Members */}
              <FormField
                control={control}
                name="ofNscsMembers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of NSCS Members *</FormLabel>
                    <FormControl>
                      <Input
                        min="0"
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Non-NSCS Members */}
              <FormField
                control={control}
                name="ofNonNscsMembers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Non-NSCS Members *</FormLabel>
                    <FormControl>
                      <Input
                        min="0"
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Advisor Present */}
            <FormField
              control={control}
              name="advisorPresent"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Advisor Present *</FormLabel>
                    <FormDescription>
                      Check this box if an advisor was present at the activity
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="min-h-[120px]"
                      placeholder="Describe the activity in detail. What was the purpose? What did members do? What were the outcomes?"
                    />
                  </FormControl>
                  <FormDescription>
                    Provide a detailed description of at least 10 characters.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Other Campus Orgs */}
            <FormField
              control={control}
              name="otherCampusOrgsInvolved"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Campus Organizations Involved</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="min-h-[80px]"
                      placeholder="List any other campus organizations that participated in this activity"
                    />
                  </FormControl>
                  <FormDescription>
                    Optional: Include any campus partners that helped with this
                    activity
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error message */}
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {/* Form actions */}
            <div className="flex justify-end gap-4 pt-4">
              <Button
                disabled={submitLoading}
                onClick={handleCancel}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={submitLoading} type="submit">
                {submitLoading ? "Submitting..." : "Submit Activity"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </MainPageWrapper>
  );
};

export default SubmitChapterActivityForm;
